/**
 * Shared formatters for Supplier Reconciliation module
 * Consolidates currency and other formatting utilities
 */

import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber } from '@gravitate-js/excalibrr'
import type { ValueFormatterParams } from 'ag-grid-community'

/**
 * Hook for getting dynamic currency formatters that respect market profile settings
 */
export function useCurrencyFormatters() {
  const { formatCurrency: formatCurrencyHelper } = useFormatter()
  const currencySymbol = formatCurrencyHelper(null, true)

  return {
    /**
     * Format currency with dynamic symbol
     */
    formatCurrency: (amount?: number): string => {
      if (amount === null || amount === undefined) return `${currencySymbol}0.00`
      return `${currencySymbol}${addCommasToNumber(amount, 2)}`
    },

    /**
     * AG-Grid currency formatter wrapper
     */
    currencyFormatter: (params: ValueFormatterParams): string => {
      if (params.value === undefined || params.value === null) return ''
      return `${currencySymbol}${addCommasToNumber(params.value, 2)}`
    },

    /**
     * Format currency with variance display (positive/negative)
     */
    formatVariance: (value: number, showSign = true): string => {
      if (value === 0) return `${currencySymbol}0.00`
      const formatted = `${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
      return showSign && value !== 0 ? `${value > 0 ? '+' : '-'}${formatted}` : formatted
    },

    /**
     * Format per-unit prices (4 decimal places)
     */
    formatPerUnit: (amount?: number): string => {
      if (amount === null || amount === undefined) return `${currencySymbol}0.0000`
      return `${currencySymbol}${amount.toFixed(4)}`
    },

    /**
     * Get the currency symbol
     */
    currencySymbol,
  }
}

/**
 * DEPRECATED: Legacy formatters - use useCurrencyFormatters() hook instead
 * These are kept for backward compatibility but should be migrated
 */

export const currencyFormatter = (params: ValueFormatterParams): string => {
  if (params.value === undefined || params.value === null) return ''
  // DEPRECATED: This uses hardcoded USD formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(params.value)
}

export const formatVariance = (value: number, showSign = true): string => {
  if (value === 0) return '$0.00'
  // DEPRECATED: This uses hardcoded $ symbol
  const formatted = `$${addCommasToNumber(Math.abs(value), 2)}`
  return showSign && value !== 0 ? `${value > 0 ? '+' : '-'}${formatted}` : formatted
}

export const formatPerUnit = (amount?: number): string => {
  if (amount === null || amount === undefined) return '$0.0000'
  // DEPRECATED: This uses hardcoded USD formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(amount)
}

export const formatCurrency = (amount?: number): string => {
  if (amount === null || amount === undefined) return '$0.00'
  // DEPRECATED: This uses hardcoded USD formatting
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount)
}
